import { cache } from "react"
import { i18nConfig } from "@/i18Config"
import type { Elements, IContentItem } from "@kontent-ai/delivery-sdk"

import { Pub } from "@/types/kontent-generated"
import { IKTImage, IKTRichText } from "@/types/kontent.types"
import { getDeliveryClient } from "@/lib/kontent/client"
import { resolveRichText } from "@/lib/kontent/richText"

import { assertItemResponse, fetchKontent } from "../helpers"
import { mapAssetToIKTImage, mapRichTextElementToIKTRichText } from "../mappers"

export type TResolvedPub = {
  id: string
  title: string
  image: IKTImage
  text: IKTRichText
  backgroundColor: string
}

/**
 * Maps an SDK Pub content item to TResolvedPub.
 */
export async function _mapPubItem(
  item: Pub,
  previewMode: boolean
): Promise<TResolvedPub> {
  return {
    id: item.system.id,
    title: item.elements.title.value,
    image: mapAssetToIKTImage(item.elements.image.value[0]),
    text: await resolveRichText(
      mapRichTextElementToIKTRichText(
        item.elements.text as Elements.RichTextElement<IContentItem>
      ),
      previewMode
    ),
    backgroundColor: item.elements.background_color.value[0]?.codename ?? "",
  }
}

/**
 * Maps an already-fetched SDK item, avoiding a redundant Delivery API call
 * when the item was already resolved by a parent query's depth parameter.
 */
export async function GetPubFromItem(
  rawItem: IContentItem,
  previewMode: boolean
): Promise<TResolvedPub> {
  return _mapPubItem(rawItem as Pub, previewMode)
}

async function _fetchPub(
  codename: string,
  previewMode = false
): Promise<TResolvedPub> {
  return fetchKontent("Pub", async () => {
    const client = getDeliveryClient(previewMode)
    const response = await client
      .item<Pub>(codename)
      .languageParameter(i18nConfig.defaultLocale)
      .depthParameter(1)
      .toPromise()

    const item = assertItemResponse(response, "Pub")

    return await _mapPubItem(item, previewMode)
  })
}

export const GetPub = cache(_fetchPub)
